import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, DistributiveOmit, If, Row, StyleProps } from "@hex-insights/core";
import {
	FieldConditional,
	FieldElse,
	FieldIf,
	Form,
	FormEditButtonPair,
	FormState,
	SubmitButton,
} from "@hex-insights/forms";
import {
	Contract,
	PaymentCreateMutation,
	PaymentDetailQuery,
	PaymentUpdateMutation,
	Student,
} from "../../../Utilities";
import { ModelFormat } from "../../Format";
import { PaymentFormState, PaymentFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<PaymentCreateMutation["createPayment"]>,
	"formState" | "create"
> & {
	formStateArgs?: PaymentFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the Payment model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	studentID?: Student["id"];
	contractID?: Contract["id"];
	formState: FormState<PaymentFormValues.Create>;
	create: CreateFunction<PaymentFormValues.Create, T>;
	onSuccess: (formState: FormState<PaymentFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<PaymentFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the Payment model using the given form state.
 */
export function Create<T>({
	studentID,
	contractID,
	formState,
	create,
	onSuccess,
	formNameSuffix,
	...props
}: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "payment.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column style={{ width: "100%" }}>
				<CreateFields formState={formState} studentID={studentID} contractID={contractID} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type CreateFieldsProps = {
	formState: FormState<PaymentFormValues.Create>;
	studentID?: Student["id"];
	contractID?: Contract["id"];
};

function CreateFields({ formState, studentID, contractID }: CreateFieldsProps) {
	const guardianQueryOptions = React.useMemo<Field.GuardianPayer.SelectProps["queryOptions"]>(() => {
		if (!studentID) {
			return undefined;
		}
		return {
			variables: {
				filters: { relationships: [{ studentIDEQ: studentID }] },
			},
		};
	}, [studentID]);

	const totalLocalCurrencyPaymentAmount = formState.formValues.paymentLineItems.reduce(
		(a, e) => a + (e.localAmount ?? 0),
		0,
	);
	const totalPaymentAmount = formState.formValues.paymentLineItems.reduce((a, e) => a + (e.amount ?? 0), 0);

	return (
		<React.Fragment>
			<FieldConditional>
				<FieldIf formState={formState} name="customPayerName" condition={formState.formValues.isCustomPayer}>
					<Field.CustomPayerName formState={formState} />
				</FieldIf>
				<FieldElse formState={formState} name="guardianPayerID">
					<Field.GuardianPayer.Select formState={formState} queryOptions={guardianQueryOptions} />
				</FieldElse>
			</FieldConditional>
			<Field.IsCustomPayer formState={formState} />
			<Field.IssueDate formState={formState} />
			<Field.PaymentMethod formState={formState} />
			<Field.IsInLocalCurrency formState={formState} />
			<FieldIf formState={formState} name="localCurrency" condition={formState.formValues.isInLocalCurrency}>
				<Field.LocalCurrency formState={formState} />
			</FieldIf>
			{/* TODO filtering by student(s) for invoices */}
			<Field.PaymentLineItems.SubForm
				formState={formState}
				studentID={studentID}
				contractID={contractID}
				style={{ width: "100%" }}
			/>
			<Row justify="flex-end">
				<table style={{ fontWeight: "bold" }}>
					<tbody>
						<If condition={formState.formValues.isInLocalCurrency}>
							<tr style={{ color: "var(--neutral-colors--secondary)" }}>
								<td style={{ paddingRight: "0.25rem" }}>Alt. Currency:</td>
								<td style={{ textAlign: "right" }}>
									{ModelFormat.money(totalLocalCurrencyPaymentAmount, formState.formValues.localCurrency)}
								</td>
							</tr>
						</If>
						<tr style={{ fontSize: "1.25rem" }}>
							<td style={{ paddingRight: "0.25rem" }}>Total:</td>
							<td style={{ textAlign: "right" }}>{ModelFormat.money(totalPaymentAmount)}</td>
						</tr>
					</tbody>
				</table>
			</Row>
			<Field.Notes formState={formState} style={{ width: "100%" }} />
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<PaymentUpdateMutation["updatePayment"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<PaymentFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the Payment model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({ ...formStateArgs, payment: props.payment });

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	payment: PaymentDetailQuery["payment"];
	formState: FormState<PaymentFormValues.Detail>;
	update: UpdateFunction<PaymentFormValues.Detail, T>;
	onSuccess: (formState: FormState<PaymentFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<PaymentFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the Payment model using the given form state.
 */
export function Detail<T>({ formState, payment, update, onSuccess, formNameSuffix, ...props }: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `payment.detail.${payment.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} payment={payment} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<PaymentFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the Payment model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, payment: props.payment });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	payment: PaymentDetailQuery["payment"];
	formState: FormState<PaymentFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the Payment model using the given form state.
 */
export function ReadOnly({ formState, payment, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} payment={payment} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	payment: PaymentDetailQuery["payment"];
	formState: FormState<PaymentFormValues.Detail>;
};

function DetailFields({ formState, payment }: DetailFieldsProps) {
	return (
		<React.Fragment>
			<Field.IsCustomPayer formState={formState} />
			<Field.CustomPayerName formState={formState} />
			<Field.IssueDate formState={formState} />
			<Field.PaymentMethod formState={formState} />
			<Field.IsInLocalCurrency formState={formState} />
			<FieldIf formState={formState} name="localCurrency" condition={formState.formValues.isInLocalCurrency}>
				<Field.LocalCurrency formState={formState} />
			</FieldIf>
			<Field.Notes formState={formState} />
			<Field.GuardianPayer.Select formState={formState} currentInstance={payment.guardianPayer} />
		</React.Fragment>
	);
}
